import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Brain, RefreshCw, Plug, Loader2, Mail } from "lucide-react";

const errMsg = (e) => e?.response?.data?.detail || e?.message || "Σφάλμα";
const when = (iso) => (iso ? new Date(iso).toLocaleString("el-GR") : "—");

export const AgentStatus = ({ cfg, reload }) => {
  const [busy, setBusy] = useState("");
  const tr = cfg.training || {};
  const act = async (key, fn, ok) => {
    setBusy(key);
    try { const r = await fn(); toast.success(typeof ok === "function" ? ok(r.data) : ok); reload(); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(""); }
  };
  const toggle = (v) => act("enabled", () => api.patch("/email-agent", { enabled: v }), v ? "Ο agent ενεργοποιήθηκε" : "Ο agent απενεργοποιήθηκε");
  return (
    <section className="border border-slate-200 rounded-lg p-5 bg-white space-y-4" data-testid="email-agent-status">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm text-slate-700">
          <Mail className="w-4 h-4 text-[#0052CC]" />
          <span data-testid="email-agent-mailbox">{cfg.mailbox || "Δεν έχει ρυθμιστεί mailbox"}</span>
        </div>
        <label className="flex items-center gap-2 text-sm font-medium text-slate-800">
          Αυτόματος έλεγχος κάθε 15′
          <Switch checked={!!cfg.enabled} onCheckedChange={toggle} disabled={busy === "enabled"} data-testid="email-agent-enabled-switch" />
        </label>
      </div>
      <div className="grid sm:grid-cols-3 gap-3 text-xs">
        <Stat label="Εκπαίδευση" value={tr.message || "Δεν έχει γίνει"} testid="email-agent-training-status" spin={tr.status === "running"} />
        <Stat label="Τελευταίος έλεγχος" value={when(cfg.last_check_at)} testid="email-agent-last-check" />
        <Stat label="Πρόχειρα / Παραλείφθηκαν" value={`${cfg.stats?.drafted || 0} / ${cfg.stats?.skipped || 0}`} testid="email-agent-stats" />
      </div>
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" size="sm" disabled={!!busy} data-testid="email-agent-test-btn"
          onClick={() => act("test", () => api.post("/email-agent/test-connection"), (d) => `Σύνδεση OK · ${d.counts.inbox} εισερχόμενα · ${d.counts.sent || 0} απεσταλμένα`)}>
          {busy === "test" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plug className="w-4 h-4 mr-1" />} Δοκιμή σύνδεσης
        </Button>
        <Button variant="outline" size="sm" disabled={!!busy || tr.status === "running"} data-testid="email-agent-train-btn"
          onClick={() => window.confirm("Ο agent θα διαβάσει όλα τα email (εισερχόμενα & απεσταλμένα) για να μάθει ύφος και γνώσεις. Κόστος AI περίπου $0.10–1 ανάλογα με τον όγκο. Συνέχεια;") && act("train", () => api.post("/email-agent/train"), "Η εκπαίδευση ξεκίνησε")}>
          <Brain className="w-4 h-4 mr-1" /> {cfg.profile ? "Επανεκπαίδευση" : "Εκπαίδευση"}
        </Button>
        <Button size="sm" disabled={!!busy} data-testid="email-agent-check-btn"
          onClick={() => act("check", () => api.post("/email-agent/check-now"), "Ο έλεγχος ξεκίνησε").then(() => [5000, 15000, 40000].forEach((ms) => setTimeout(reload, ms)))}>
          <RefreshCw className="w-4 h-4 mr-1" /> Έλεγχος τώρα
        </Button>
      </div>
      {cfg.last_error && <p className="text-xs text-rose-700 bg-rose-50 border border-rose-100 rounded p-2" data-testid="email-agent-last-error">Τελευταίο σφάλμα: {cfg.last_error}</p>}
      {!cfg.last_uid && cfg.last_uid !== 0 && (
        <p className="text-[11px] text-slate-500" data-testid="email-agent-first-run-note">
          Στον πρώτο έλεγχο ο agent ετοιμάζει πρόχειρα για τα αδιάβαστα των τελευταίων 7 ημερών και μετά για κάθε νέο email.
        </p>
      )}
    </section>
  );
};

const Stat = ({ label, value, testid, spin }) => (
  <div className="rounded-md border border-slate-100 bg-slate-50/70 p-2.5">
    <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500 font-semibold">{label}</div>
    <div className="mt-1 text-slate-800 flex items-center gap-1" data-testid={testid}>{spin && <Loader2 className="w-3 h-3 animate-spin" />}{value}</div>
  </div>
);
